import { useState } from 'react'
import type { LigneDevis } from '../db/modele'
import { lignesDeRemise, pourcentageValide } from '../lib/remise'
import { formatEuro, lireNombre, centimesVersEuros } from '../lib/money'
import { Bouton } from './composants/bouton'
import { Feuille } from './composants/feuille'
import { CHAMP } from './synchro'

/**
 * Remise commerciale en pourcentage, sur une ligne (`cible`) ou sur tout le devis : l'aperçu montre les lignes qui
 * seront ajoutées (une par taux de TVA pour une remise générale). Elle est figée à l'ajout : si les lignes changent
 * ensuite, on la refait.
 */
export function FeuilleRemise(props: { lignes: LigneDevis[]; cible?: number; appliquer: (pourcent: string) => Promise<void>; fermer: () => void }) {
  const [saisie, setSaisie] = useState('10')
  const nombre = lireNombre(saisie)
  const pourcent = nombre && pourcentageValide(nombre)
  const apercu = pourcent ? lignesDeRemise(props.lignes, pourcent, props.cible) : []
  const titre = props.cible === undefined ? 'Remise sur tout le devis' : `Remise sur « ${props.lignes[props.cible]?.designation ?? ''} »`
  const total = apercu.reduce((n, l) => n + l.prixUnitaireHtCents, 0)

  return (
    <Feuille ouverte onChange={(o) => !o && props.fermer()} titre={titre}>
      <div className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-sm text-slate-700">Remise (%)</span>
          <input className={CHAMP} inputMode="decimal" value={saisie} onChange={(e) => setSaisie(e.target.value)} aria-label="Pourcentage de remise" />
        </label>
        {!pourcent && saisie.trim() !== '' && <p className="text-sm text-red-800" role="alert">Entrez un pourcentage de 0,01 à 100 (deux décimales au plus).</p>}
        {pourcent && apercu.length === 0 && <p className="text-sm text-slate-700">Rien à remiser (montant nul).</p>}
        {apercu.length > 0 && (
          <ul className="flex flex-col gap-1 text-sm" aria-label="Lignes de remise">
            {apercu.map((l) => (
              <li key={l.designation} className="flex justify-between gap-3 rounded bg-slate-100 p-2">
                <span className="min-w-0">{l.designation}</span>
                <span className="shrink-0 font-semibold">− {formatEuro(centimesVersEuros(l.prixUnitaireHtCents))} HT</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-sm text-slate-700">La remise est ajoutée comme une ligne (quantité −1). Si vous modifiez les lignes ensuite, elle ne se recalcule pas : supprimez-la et refaites-la.</p>
        <Bouton
          disabled={apercu.length === 0}
          onClick={async () => {
            await props.appliquer(saisie)
            props.fermer()
          }}
        >
          Ajouter la remise{total > 0 ? ` de ${formatEuro(centimesVersEuros(total))} HT` : ''}
        </Bouton>
      </div>
    </Feuille>
  )
}
